<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2019-2023 (original work) Open Assessment Technologies SA ;

    import { __, generateElementId } from '@oat-sa-private/ui-core';
    import { onMount, onDestroy } from 'svelte';
    import flatpickr from 'flatpickr';
    import { Icon, BlockFeedback } from '@oat-sa-private/ui-elements';
    import getL10n from './l10n.js';

    import './css/index.css';

    /**
     * Different widths of input field
     */
    const widths = {
        small: 'small',
        medium: 'medium',
        large: 'large',
        flexible: 'flexible'
    };
    export let placeholder = __('Select a date');
    export let width = widths.small;
    export let options = {};
    export let value = '';
    export let customValidity = { valid: true };
    export let disabled = false;

    let validity = {
        messages: [],
        overallValidity: true
    };

    $: validate(customValidity);

    let datePicker = null;
    let datepickerId = generateElementId('datetimepicker');

    onMount(() => {
        getL10n(__.getLocale()).then(locale => {
            datePicker = flatpickr(`input#${datepickerId}`, {
                locale,
                ...options
            });
        });
        __.addEventListener('localechange', onLocaleUpdate);
    });

    onDestroy(() => {
        //check if datePicker is created, onDestroy may be called before getL10n promise resolves
        if (datePicker) {
            datePicker.destroy();
        }
        __.removeEventListener('localechange', onLocaleUpdate);
    });

    function onLocaleUpdate({ detail: { newLocale } }) {
        getL10n(newLocale).then(locale => {
            datePicker.set('locale', locale);
        });
    }

    function validate() {
        // Start in valid state
        validity = {
            messages: [],
            overallValidity: true
        };

        if (customValidity && !customValidity.valid) {
            validity.messages = [customValidity];
            validity.overallValidity = customValidity.valid;
        }
    }
</script>

<style>
    .datetime {
        position: relative;
        display: inline-block;
        height: 4.8rem;
    }

    .flexible {
        width: 100%;
    }

    .large {
        width: 48rem;
    }

    .medium {
        width: 32rem;
    }

    .small {
        width: 18rem;
    }

    input {
        width: 100%;
        height: 100%;
        padding: 0 5.6rem 0 1rem;
        border: var(--border-thin) solid var(--color-border-default);
        font-size: 1.6rem;

        &:disabled {
            background: var(--color-bg-disabled-subtle);
            cursor: not-allowed;
        }
    }

    .icon-wrapper {
        position: absolute;
        right: calc(2 * var(--border-thin)); /* border + shadow */
        top: calc(2 * var(--border-thin));
        height: calc(100% - 4 * var(--border-thin));
        width: 4.8rem;
        border-left: var(--border-thin) solid var(--color-border-default);
        background: var(--color-bg-input-control);
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: none;
    }

    .invalid input {
        border: var(--border-thin) solid var(--color-border-warning);
        box-shadow: 0 0 0 var(--border-thin) var(--color-border-warning) inset;
    }
</style>

<!-- svelte-ignore a11y-label-has-associated-control -->
<label>
    <div class="datetime {width}" class:invalid={validity.messages.length}>
        <input id={datepickerId} class="datetime" {placeholder} {value} {disabled} />
        <div class="icon-wrapper">
            <Icon name="date-16" />
        </div>
    </div>
    {#if validity.messages.length > 0}
        <BlockFeedback {validity} firstTry={false} />
    {/if}
</label>
